import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import useAuth from "../../hooks/useAuth";
import { loadEncrypted, saveEncrypted } from "../../utils/encryptedStorage";
import { CartContextType, CartProviderProps, CartState, Product } from "./cartType";


const DEFAULT_STATE: CartState = { products: [], freightCost: 0, guidCart: "" };
const STORAGE_KEY = 'checkout_cart';
const PLUGINSPACE_STORAGE_KEY = 'pluginspace_context_guard';
const PLUGINSPACE_CHANGE_EVENT = 'pluginspace:change';

export const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: CartProviderProps) {
    const { currentPluginspaceId } = useAuth();
    const [state, setState] = useState<CartState>(() => {
        // Carregar do localStorage na inicializa\u00e7\u00e3o
        const saved = loadEncrypted<CartState>(STORAGE_KEY);
        return saved || DEFAULT_STATE;
    });

    // ============ PERSIST\u00caNCIA ============

    useEffect(() => {
        // Salvar no localStorage sempre que o estado mudar
        saveEncrypted(STORAGE_KEY, state);
    }, [state]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handlePluginspaceChange = (event: Event) => {
            const customEvent = event as CustomEvent<{ pluginspaceId: number }>;
            const newPluginspaceId = customEvent.detail?.pluginspaceId;
            if (!newPluginspaceId) return;

            const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
            const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
            const nextId = String(newPluginspaceId);

            if (storedPluginspaceId && storedPluginspaceId !== nextId) {
                setState(DEFAULT_STATE);
            }

            if (storedPluginspaceId !== nextId) {
                saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: newPluginspaceId });
            }
        };

        window.addEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
        return () => window.removeEventListener(PLUGINSPACE_CHANGE_EVENT, handlePluginspaceChange as EventListener);
    }, []);

    useEffect(() => {
        if (!currentPluginspaceId) return;
        const stored = loadEncrypted<{ pluginspaceId: number }>(PLUGINSPACE_STORAGE_KEY);
        const storedPluginspaceId = stored?.pluginspaceId ? String(stored.pluginspaceId) : null;
        const currentId = String(currentPluginspaceId);

        if (storedPluginspaceId && storedPluginspaceId !== currentId) {
            setState(DEFAULT_STATE);
        }

        if (storedPluginspaceId !== currentId) {
            saveEncrypted(PLUGINSPACE_STORAGE_KEY, { pluginspaceId: currentPluginspaceId });
        }
    }, [currentPluginspaceId]);

    // ============ VALORES COMPUTADOS (MEMOIZADOS) ============

    const cartCount = useMemo(() => {
        return state.products.reduce((sum, product) => sum + parseInt(product.quantity.toString(), 10), 0);
    }, [state.products]);

    const subtotal = useMemo(() => {
        return state.products.reduce((sum, product) => {
            let productPrice = product.unitPrice;

            return sum + (productPrice * parseInt(product.quantity.toString(), 10));
        }, 0);
    }, [state.products]);

    const subtotalWithDiscount = useMemo(() => {
        return state.products.reduce((sum, product) => {
            let productPrice = product.promotionalPrice > 0 ? product.promotionalPrice : product.unitPrice;

            return sum + (productPrice * parseInt(product.quantity.toString(), 10));
        }, 0);
    }, [state.products]);

    // ============ A\u00c7\u00d5ES (MEMOIZADAS) ============

    const addProduct = useCallback((product: Omit<Product, 'quantity'> & { quantity?: number }) => {
        setState(prev => {
            const existingProduct = prev.products.find(p => {
                // Verifica se é o mesmo produto com as mesmas opções/grade/variação
                const sameId = p.id === product.id;
                const sameMeasures = JSON.stringify(p.measures) === JSON.stringify(product.measures);
                const sameSku = JSON.stringify(p.sku) === JSON.stringify(product.sku);
                const sameVariation = JSON.stringify(p.variation) === JSON.stringify(product.variation);

                return sameId && sameMeasures && sameSku && sameVariation;
            });

            if (existingProduct) {
                // Incrementar quantidade do produto existente
                return {
                    ...prev,
                    products: prev.products.map(p =>
                        p === existingProduct
                            ? { ...p, quantity: p.quantity + (product.quantity || 1) }
                            : p
                    ),
                };
            } else {
                // Adicionar novo produto
                return {
                    ...prev,
                    products: [
                        ...prev.products,
                        { ...product, quantity: product.quantity || 1 } as Product,
                    ],
                };
            }
        });
    }, []);

    const addProducts = useCallback((products: Array<Omit<Product, 'quantity'> & { quantity?: number }>) => {
        if (!products || products.length === 0) return;

        setState(prev => {
            let nextProducts = [...prev.products];

            products.forEach(product => {
                const existingIndex = nextProducts.findIndex(p => {
                    // Verifica se é o mesmo produto com as mesmas opções/grade/variação
                    const sameId = p.id === product.id;
                    const sameMeasures = JSON.stringify(p.measures) === JSON.stringify(product.measures);
                    const sameSku = JSON.stringify(p.sku) === JSON.stringify(product.sku);
                    const sameVariation = JSON.stringify(p.variation) === JSON.stringify(product.variation);

                    return sameId && sameMeasures && sameSku && sameVariation;
                });

                if (existingIndex >= 0) {
                    const existing = nextProducts[existingIndex];
                    nextProducts[existingIndex] = {
                        ...existing,
                        quantity: product.quantity || 1,
                    };
                } else {
                    nextProducts.push({ ...product, quantity: product.quantity || 1 } as Product);
                }
            });

            return {
                ...prev,
                products: nextProducts,
            };
        });
    }, []);

    const updateCartWithProducts = useCallback((products: Product[]) => {
        setState(prev => ({
            ...prev,
            products,
        }));
    }, []);

    const removeProduct = useCallback((product: Omit<Product, 'quantity'>, context?: string) => {
        setState(prev => ({
            ...prev,
            products: prev.products.filter(p => {
                const sameId = p.id === product.id;
                const sameMeasures = JSON.stringify(p.measures) === JSON.stringify(product.measures);
                const sameSku = JSON.stringify(p.sku) === JSON.stringify(product.sku);
                const sameVariation = JSON.stringify(p.variation) === JSON.stringify(product.variation);
                return !(sameId && sameMeasures && sameSku && sameVariation);
            }),
        }));
    }, []);

    const updateQuantity = useCallback((product: Omit<Product, 'quantity'>, quantity: number) => {
        if (quantity <= 0) {
            removeProduct(product);
            return;
        }

        setState(prev => ({
            ...prev,
            products: prev.products.map(p => {
                // Verifica se é o mesmo produto com as mesmas opções/grade/variação
                const sameId = p.id === product.id;
                const sameMeasures = JSON.stringify(p.measures) === JSON.stringify(product.measures);
                const sameSku = JSON.stringify(p.sku) === JSON.stringify(product.sku);
                const sameVariation = JSON.stringify(p.variation) === JSON.stringify(product.variation);
                return sameId && sameMeasures && sameSku && sameVariation ? { ...p, quantity } : p
            }
            ),
        }));
    }, [removeProduct]);

    const updateObservation = useCallback((productId: string, observation: string) => {
        setState(prev => ({
            ...prev,
            products: prev.products.map(p =>
                p.id.toString() === productId ? { ...p, observation } : p
            ),
        }));
    }, []);

    const clearCart = useCallback(() => {
        setState({ products: [], freightCost: 0, guidCart: "" });
    }, []);

    const setFreightCost = useCallback((cost: number) => {
        setState(prev => ({ ...prev, freightCost: cost }));
    }, []);

    const addGuidCart = useCallback((guidCart: string) => {
        setState(prev => ({ ...prev, guidCart }));
    }, []);

    const removeGuidCart = useCallback(() => {
        setState(prev => ({ ...prev, guidCart: "" }));
    }, []);

    // ============ QUERIES (MEMOIZADAS) ============

    const getProduct = useCallback((productId: string) => {
        return state.products.find(p => p.id.toString() === productId);
    }, [state.products]);

    const hasProduct = useCallback((productId: string) => {
        return state.products.some(p => p.id.toString() === productId);
    }, [state.products]);

    // ============ VALUE (MEMOIZADO) ============

    const value = useMemo<CartContextType>(() => ({
        // Estado
        products: state.products,
        subtotal,
        subtotalWithDiscount,
        cartCount,
        freightCost: state.freightCost,
        guidCart: state.guidCart,

        // Funcoes
        addProduct,
        removeProduct,
        updateQuantity,
        updateObservation,
        clearCart,
        setFreightCost,
        addProducts,
        updateCartWithProducts,
        addGuidCart,
        removeGuidCart,

        // Queries
        getProduct,
        hasProduct,
    }), [
        state.products,
        state.freightCost,
        subtotal,
        subtotalWithDiscount,
        cartCount,
        state.guidCart,
        addProduct,
        removeProduct,
        updateQuantity,
        updateObservation,
        clearCart,
        addProducts,
        setFreightCost,
        addGuidCart,
        removeGuidCart,
        getProduct,
        hasProduct,
        updateCartWithProducts,
    ]);

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}


const useCart = () => {
    const context = useContext(CartContext)
    if (!context) throw new Error("useCart must be used within CartProvider");
    return context
}
export default useCart

/**
 * Hook seletor - apenas contagem de itens
 * Re-renderiza APENAS quando o count mudar
 */
export function useCartCount() {
  const { cartCount } = useCart();
  return useMemo(() => cartCount, [cartCount]);
}

/**
 * Hook seletor - apenas produtos
 * Re-renderiza APENAS quando a lista de produtos mudar
 */
export function useCartProducts() {
  const { products } = useCart();
  return useMemo(() => products, [products]);
}

/**
 * Hook seletor - apenas subtotal
 * Re-renderiza APENAS quando o subtotal mudar
 */
export function useCartSubtotal() {
  const { subtotal } = useCart();
  return useMemo(() => subtotal, [subtotal]);
}

/**
 * Hook seletor - apenas custo de frete
 * Re-renderiza APENAS quando o frete mudar
 */
export function useCartFreight() {
  const { freightCost } = useCart();
  return useMemo(() => freightCost, [freightCost]);
}

/**
 * Hook seletor - total (subtotal + frete)
 * Re-renderiza APENAS quando subtotal ou frete mudarem
 */
export function useCartTotal() {
  const { subtotal, freightCost } = useCart();
  return useMemo(() => subtotal + freightCost, [subtotal, freightCost]);
}

export function useCartWithDiscount() {
  const { subtotalWithDiscount } = useCart();
  return useMemo(() => subtotalWithDiscount, [subtotalWithDiscount]);
}

export function useCartWithDiscountAndFreight() {
  const { subtotalWithDiscount, freightCost } = useCart();
  return useMemo(() => subtotalWithDiscount + freightCost, [subtotalWithDiscount, freightCost]);
}

/**
 * Hook seletor - apenas a\u00e7\u00f5es
 * NUNCA re-renderiza (fun\u00e7\u00f5es s\u00e3o memoizadas)
 */
export function useCartActions() {
  const {
    addProduct,
    addProducts,
    removeProduct,
    updateQuantity,
    updateObservation,
    clearCart,
    setFreightCost,
  } = useCart();

  return useMemo(() => ({
    addProduct,
    addProducts,
    removeProduct,
    updateQuantity,
    updateObservation,
    clearCart,
    setFreightCost,
  }), [
    addProduct,
    addProducts,
    removeProduct,
    updateQuantity,
    updateObservation,
    clearCart,
    setFreightCost,
  ]);
}

/**
 * Hook seletor - queries
 * NUNCA re-renderiza (fun\u00e7\u00f5es s\u00e3o memoizadas)
 */
export function useCartQueries() {
  const { getProduct, hasProduct } = useCart();

  return useMemo(() => ({
    getProduct,
    hasProduct,
  }), [getProduct, hasProduct]);
}

/**
 * Hook seletor - verifica se carrinho est\u00e1 vazio
 * Re-renderiza APENAS quando o estado vazio mudar
 */
export function useCartIsEmpty() {
  const { cartCount } = useCart();
  return useMemo(() => cartCount === 0, [cartCount]);
}
